import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError, API_BASE } from "../lib/api";
import { useFlash } from "../lib/flash";
import { formatKr, fmtTime, fmtDateTime, slotsOf, fmtSlot, isToday } from "../lib/format";

const NAME_OK = /^[A-Za-zÀ-ÖØ-öø-ÿ][A-Za-zÀ-ÖØ-öø-ÿ' -]*[A-Za-zÀ-ÖØ-öø-ÿ]$/;

// Facebook didn't send the commenter's name: the customer can type it so the shop finds the order
function NameCard({ token, customer, onSaved }) {
  const mine = customer.name_source === "customer";
  const [editing, setEditing] = useState(!mine);
  const [name, setName] = useState(mine ? customer.name : "");
  const [err, setErr] = useState("");
  const [thanks, setThanks] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    const v = name.split(/\s+/).filter(Boolean).join(" ");
    if (v.length < 2 || v.length > 60 || !NAME_OK.test(v)) return setErr("Skriv dit navn med bogstaver (2–60 tegn).");
    setBusy(true);
    setErr("");
    try {
      const r = await api.post(`/o/${token}/name`, { name: v });
      setThanks(r.data.flash);
      setEditing(false);
      onSaved();
    } catch (ex) {
      setErr(apiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };
  if (!editing)
    return (
      <div className="name-card done" data-testid="name-card-done">
        <span>{thanks || `Butikken kender dig som ${customer.name}.`}</span>
        <button type="button" className="linklike" onClick={() => { setName(customer.name); setEditing(true); }} data-testid="name-card-edit">Ret navn</button>
      </div>
    );
  return (
    <form className="name-card" onSubmit={save} data-testid="name-card">
      <p className="name-card-title">Hjælp butikken med at finde din ordre – skriv dit navn</p>
      <div className="name-card-row">
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Fornavn og efternavn" maxLength={60}
          autoComplete="name" aria-label="Dit navn" data-testid="name-card-input" />
        <button className="btn jatak" disabled={busy} data-testid="name-card-save">Gem</button>
      </div>
      {err && <p className="name-card-error" data-testid="name-card-error">{err}</p>}
    </form>
  );
}

function PickupDays({ offer }) {
  const sl = slotsOf(offer);
  const now = Date.now();
  const next = sl.find((s) => Date.parse(s.end) > now);
  return (
    <ul className="pickup-days">
      {sl.map((s, i) => {
        const past = Date.parse(s.end) <= now;
        const today = isToday(s.start);
        return (
          <li key={i} className={`${s === next ? "next" : ""}${past ? " past" : ""}`} data-testid={`order-slot-${i}`}>
            {fmtSlot(s)}
            {today && !past && <span className="pd-tag">I dag</span>}
            {s === next && !today && sl.length > 1 && <span className="pd-tag soft">Næste</span>}
          </li>
        );
      })}
    </ul>
  );
}
import { Pill } from "../components/bits";
import { QRCodeSVG } from "qrcode.react";

export default function CustomerOrder() {
  const { token } = useParams();
  const flash = useFlash();
  const [d, setD] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [phone, setPhone] = useState("");

  const load = useCallback(() => {
    api
      .get(`/o/${token}`)
      .then((r) => {
        setD(r.data);
        setPhone(r.data.customer.phone ? r.data.customer.phone.replace(/^45/, "") : "");
      })
      .catch(() => setNotFound(true));
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  if (notFound)
    return (
      <div className="customer">
        <h1>Siden findes ikke</h1>
        <p>Linket er måske forkert eller udløbet.</p>
      </div>
    );
  if (!d) return <div className="loading">Indlæser…</div>;

  const { order, offer, customer } = d;
  const active = ["reserved", "pending_confirm", "waitlist"].includes(order.status);
  const pickupOver = new Date() > new Date(offer.pickup_end);
  const qty = order.status === "waitlist" ? order.requested_qty : order.qty;

  const nameless = customer.name_editable && customer.name_source !== "customer";
  const headline = {
    reserved: nameless ? "Din vare er reserveret" : `${(customer.name || "").split(" ")[0]}, din vare er reserveret`,
    pending_confirm: "Bekræft din reservation",
    waitlist: `Du er nr. ${d.position} på ventelisten`,
    picked_up: "Hentet – tak for handlen!",
    cancelled: "Reservationen er annulleret",
    expired: "Reservationen er udløbet",
    no_show: "Varen blev ikke hentet",
  }[order.status] || d.status_label;

  const act = async (action, body) => {
    try {
      const r = await api.post(`/o/${token}/${action}`, body);
      if (r.data.flash) flash(r.data.flash, r.data.ok === false ? "error" : "");
      load();
    } catch (err) {
      flash(apiError(err.response?.data?.detail) || err.message, "error");
    }
  };

  return (
    <div className="customer">
      <div className="co-brand" data-testid="order-brand"><img src="/logo.svg?v=2" alt="" />JaTak Pro</div>
      <div className="store">{d.store_name}</div>
      {customer.name_editable && active && <NameCard token={token} customer={customer} onSaved={load} />}
      <h1>{headline}</h1>
      {offer.image_path && <img className="hero-img" src={offer.image_path} alt={offer.title} />}
      {["reserved", "pending_confirm"].includes(order.status) && (
        <div className="codebox">
          <span className="code xl" data-testid="order-code">{order.code}</span>
          <p>Vis koden ved kassen</p>
          <div className="order-qr" data-testid="order-qr">
            <QRCodeSVG value={`${window.location.origin}/o/${token}`} size={196} level="M" marginSize={0} title={`QR-kode for ordre ${order.code}`} />
          </div>
        </div>
      )}
      {order.status === "pending_confirm" && (
        <>
          <div className="notice warn">
            Bekræft senest kl. {fmtTime(order.confirm_deadline)} ({fmtDateTime(order.confirm_deadline)}), ellers går varen
            videre til næste på ventelisten.
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              act("confirm");
            }}
          >
            <button className="btn jatak big" style={{ width: "100%" }} data-testid="confirm-order">
              Ja, jeg henter den
            </button>
          </form>
          <br />
        </>
      )}
      {order.status === "waitlist" && (
        <p>
          Hvis nogen melder afbud, får du automatisk varen og besked på Facebook.
        </p>
      )}
      <dl>
        <dt>Vare</dt>
        <dd>
          {d.lines?.length ? (
            <span data-testid="order-lines" style={{ display: "grid", gap: 2 }}>
              {d.lines.map((l, i) => (
                <span key={i}>{l.status === "waitlist" ? l.requested_qty : l.qty} × {offer.title} – <b>{l.variant_name}</b>
                  {l.status !== order.status || l.status === "waitlist" ? <span className="muted"> ({l.status === "waitlist" ? `venteliste nr. ${l.position}` : l.status_label})</span> : null}
                </span>
              ))}
            </span>
          ) : (
            <>{qty} × {offer.title}</>
          )}
        </dd>
        <dt>Pris</dt>
        <dd>{formatKr(offer.price_ore * (d.lines?.length ? d.lines.filter((l) => ["reserved", "pending_confirm", "picked_up"].includes(l.status)).reduce((a, l) => a + l.qty, 0) : qty))}</dd>
        <dt>Afhentning</dt>
        <dd data-testid="order-pickup-slots">
          <PickupDays offer={offer} />
        </dd>
        <dt>Status</dt>
        <dd>
          <Pill status={order.status} />
        </dd>
      </dl>
      {active && !pickupOver && (
        <div className="stack">
          {order.status === "reserved" && (
            <a className="btn" href={`${API_BASE}/o/${token}/calendar.ics`}>
              Tilføj til kalender
            </a>
          )}
          {d.sms_optin && <form
            className="panel"
            data-testid="sms-optin-form"
            onSubmit={(e) => {
              e.preventDefault();
              act("phone", { phone });
            }}
          >
            <label htmlFor="phone">{customer.phone ? "SMS-påmindelse sendes til" : "Få en SMS når varen er klar"}</label>
            <div className="actions" style={{ flexWrap: "nowrap" }}>
              <input id="phone" type="tel" inputMode="tel" placeholder="12 34 56 78" value={phone} onChange={(e) => setPhone(e.target.value)} />
              <button className="btn">{customer.phone ? "Ret" : "Gem"}</button>
            </div>
            <small className="muted">Bruges kun til påmindelser om dine ja tak-varer.</small>
          </form>}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (window.confirm("Meld afbud? Varen går videre til en anden.")) act("cancel");
            }}
          >
            <button className="btn danger" style={{ width: "100%" }} data-testid="cancel-order">
              {order.status === "waitlist" ? "Forlad ventelisten" : "Jeg kan alligevel ikke – meld afbud"}
            </button>
          </form>
          <p className="muted" style={{ fontSize: 13, textAlign: "center" }}>
            Afbud er gratis og hjælper en anden kunde til varen 🙏
          </p>
        </div>
      )}
      <p className="co-legal" data-testid="order-privacy">
        <a href="/privatliv" target="_blank" rel="noreferrer" data-testid="order-privacy-link">Sådan behandler vi dine oplysninger</a>
        <span>
          <a href="/vilkaar" target="_blank" rel="noreferrer" data-testid="order-terms-link">Vilkår</a>
        </span>
      </p>
    </div>
  );
}
